import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../assets/img/roboman-logo.png';

// Social icons (inline SVGs)
const TwitterIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 4s-.7 2.1-2 3.4c1.6 1.4 2.8 3.2 3 5.2-1.4 1-3.2 1.9-5 2.4-2.2 2.8-5.3 3.9-9 3.4-3.4-.5-6-2.4-7.4-5.2-3-7.9 1.5-13.4 8.5-13.2 3.8.1 6.8 1.4 9 3.2z" /></svg>
);
const LinkedinIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
);

/**
 * PRO Footer (no hero CTA; cohesive with Header & Home)
 * - Keeps handleNav anchors & routes intact
 * - Dark outreach vibe; mesh background; compact newsletter
 * - No new dependencies
 */
const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNav = (path, sectionId = null) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior:'smooth',
    });
    if (location.pathname !== path.split('#')[0]) {
      navigate(path.split('#')[0]);
    }
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(path);
    }
  };

  return (
    <footer role="contentinfo" className="relative isolate text-slate-300">
      {/* top gradient rail */}
      <div className="h-[2px] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />

      {/* background: deep radial + mesh to match header palette */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(1200px_400px_at_80%_-100px,rgba(99,102,241,.18),transparent),radial-gradient(1000px_300px_at_0%_120%,rgba(236,72,153,.12),transparent)]" />
      <div className="absolute inset-0 -z-10 opacity-[.08] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
      <div className="bg-slate-950/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          {/* Top row: brand + quick chips (no CTA duplication) */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 transition-transform duration-300 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-md" aria-label="Go home">
              <img src={Logo} alt="Roboman" className="h-6 w-auto" />
            </button>
            <div className="flex flex-wrap gap-2">
              {['Email Sequences', 'LinkedIn Steps', 'Voice Notes', 'Proposals', 'Intent Scoring'].map((t, i) => (
                <span key={i} className="inline-flex items-center gap-2 rounded-full bg-white/5 ring-1 ring-white/10 px-3 py-1 text-xs text-slate-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Main grid */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* About & Newsletter */}
            <div className="lg:col-span-5">
              <p className="text-sm text-slate-400 max-w-md">Outreach platform for modern GTM: AI-crafted emails, LinkedIn cadences, multilingual voice notes, proposal generation, and intent scoring — all in one place.</p>
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-slate-200">Subscribe for product updates</h3>
                <form className="mt-2 flex items-stretch max-w-md" onSubmit={(e) => e.preventDefault()} aria-label="Subscribe to updates">
                  <input type="email" placeholder="Work email" className="w-full px-4 py-2 rounded-l-xl bg-slate-900/70 border border-slate-800 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                  <button type="submit" className="relative rounded-r-xl px-4 py-2 font-semibold text-white overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                    <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
                    <span className="relative">Subscribe</span>
                  </button>
                </form>
                <p className="mt-2 text-xs text-slate-500">No spam. Unsubscribe anytime.</p>
              </div>
            </div>

            {/* Link clusters */}
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
              <div>
                <h3 className="text-xs font-semibold text-slate-200 tracking-wider uppercase">Product</h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li><button onClick={() => handleNav('/', 'features')} className="hover:text-white transition-colors">Features</button></li>
                  <li><button onClick={() => handleNav('/', 'pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                  <li><button onClick={() => handleNav('/', 'testimonials')} className="hover:text-white transition-colors">Testimonials</button></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-200 tracking-wider uppercase">Company</h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li><button onClick={() => handleNav('/about')} className="hover:text-white transition-colors">About Us</button></li>
                  {/* <li><button onClick={() => handleNav('/careers')} className="hover:text-white transition-colors">Careers</button></li> */}
                  <li><button onClick={() => handleNav('/contact')} className="hover:text-white transition-colors">Contact</button></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-200 tracking-wider uppercase">Resources</h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li><button onClick={() => handleNav('/blog')} className="hover:text-white transition-colors">Blog</button></li>
                  <li><button onClick={() => handleNav('/helpcenter')} className="hover:text-white transition-colors">Help Center</button></li>
                  {/* <li><button onClick={() => handleNav('/apidocs')} className="hover:text-white transition-colors">API Docs</button></li> */}
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-200 tracking-wider uppercase">Legal</h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li><button onClick={() => handleNav('/privacypolicy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                  <li><button onClick={() => handleNav('/termsofservice')} className="hover:text-white transition-colors">Terms of Service</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* subtle divider */}
          <div className="mt-12 h-px w-full bg-gradient-to-r from-transparent via-slate-800 to-transparent" />

          {/* bottom bar */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} Roboman, Inc. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" aria-label="Twitter" className="text-slate-400 hover:text-white transition-colors"><TwitterIcon className="h-5 w-5" /></a>
              <a href="#" aria-label="LinkedIn" className="text-slate-400 hover:text-white transition-colors"><LinkedinIcon className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
